import type { TrainingProfile } from '../schema/profile';

const t = (cs: string, en: string) => ({ 'cs-CZ': cs, 'en-US': en });

/**
 * Azbest - povědomí - V1 DRAFT - authored for structural completeness; NOT a
 * substitute for sign-off by a Czech osoba odborně způsobilá v prevenci rizik.
 * Legal refs: NV 361/2007 Sb. §21 (azbest); zákon 258/2000 Sb. §41 (hlášení
 * prací s azbestem KHS). AWARENESS ONLY - recognising and NOT disturbing
 * asbestos; removal is licensed specialist work, never part of a marketplace
 * job. Bilingual (cs-CZ + en-US). Three chapters (kde na něj narazíš | proč je
 * nebezpečný | co dělat) + a graded quiz with one question per kind. Answer
 * keys live here (repo-privileged) and in the DB; stripped from the client.
 */
export const asbestosAwarenessProfile: TrainingProfile = {
  slug: 'asbestos-awareness',
  // Top-level title is a PLAIN STRING - seeded verbatim into the varchar column.
  title: 'Azbest - povědomí',
  passThreshold: 80,
  content: {
    schemaVersion: 1,
    chapters: [
      {
        id: 'ch-kde',
        title: t('Kde na azbest narazíš', 'Where you will run into asbestos'),
        minDwellSeconds: 20,
        blocks: [
          {
            id: 'h-kde',
            blockType: 'heading',
            schemaVersion: 1,
            level: 2,
            text: t('Budovy zhruba do roku 1990', 'Buildings from roughly before 1990'),
          },
          {
            id: 'p-kde-1',
            blockType: 'paragraph',
            schemaVersion: 1,
            markdown: t(
              'Azbest se v Česku běžně používal **do konce 80. let**. Typicky: **eternitová střešní krytina a fasádní šablony**, **boletické panely** (meziokenní vložky škol a úřadů), **izolace potrubí a kotlů**, staré **podlahové krytiny a lepidla**, květinové truhlíky a komínové roury.',
              'Asbestos was in common Czech use **until the late 1980s**. Typically: **corrugated "eternit" roofing and façade shingles**, **boletice panels** (window infills of schools and offices), **pipe and boiler insulation**, old **floor coverings and adhesives**, planters and flue pipes.',
            ),
          },
          {
            id: 'c-kde-pravo',
            blockType: 'callout',
            schemaVersion: 1,
            variant: 'info',
            markdown: t(
              'Právní základ: **NV 361/2007 Sb. §21** (práce s azbestem) a **zákon 258/2000 Sb. §41** - práce s azbestem se **předem hlásí hygienické stanici (KHS)** a patří specializované firmě.',
              'Legal basis: **Gov. Reg. 361/2007 Coll. §21** (asbestos work) and **Act 258/2000 Coll. §41** - asbestos work must be **reported to the public-health authority (KHS) in advance** and belongs to licensed specialists.',
            ),
          },
          {
            id: 'cl-kde',
            blockType: 'checklist',
            schemaVersion: 1,
            items: [
              t(
                'Rekonstrukce bytu v paneláku ze 70.-80. let → zpozorni',
                'Renovating a 1970s-80s flat → be alert',
              ),
              t(
                'Vlnitá šedá krytina („eternit") na střeše či kůlně',
                'Grey corrugated roofing ("eternit") on roofs and sheds',
              ),
              t(
                'Šedobílá vláknitá izolace starých trubek',
                'Grey-white fibrous insulation on old pipes',
              ),
              t(
                'Meziokenní panely starých škol a úřadů',
                'Window infill panels of old schools and offices',
              ),
            ],
          },
        ],
      },
      {
        id: 'ch-proc',
        title: t('Proč je azbest nebezpečný', 'Why asbestos is dangerous'),
        minDwellSeconds: 20,
        requiredWidgetIds: ['kc-vlakna'],
        blocks: [
          {
            id: 'h-proc',
            blockType: 'heading',
            schemaVersion: 1,
            level: 2,
            text: t(
              'Nebezpečí, které se projeví za 30 let',
              'A danger that surfaces 30 years later',
            ),
          },
          {
            id: 'p-proc-1',
            blockType: 'paragraph',
            schemaVersion: 1,
            markdown: t(
              'Nebezpečná jsou **uvolněná mikroskopická vlákna** - vdechnutá se zabodnou do plic a po desítkách let mohou způsobit **rakovinu plic a mezoteliom**. Neporušený azbest v klidu neškodí; problém vzniká, když ho **vrtáš, brousíš, řežeš nebo lámeš**. Neexistuje bezpečná dávka.',
              'The danger is **released microscopic fibres** - once inhaled they lodge in the lungs and decades later can cause **lung cancer and mesothelioma**. Undisturbed asbestos is passive; the problem starts when you **drill, sand, cut or break** it. There is no safe dose.',
            ),
          },
          {
            id: 'kc-vlakna',
            blockType: 'knowledgeCheck',
            schemaVersion: 1,
            prompt: t('Kdy se azbest stává nebezpečným?', 'When does asbestos become dangerous?'),
            choices: [
              { id: 'kc-a', label: t('Jakmile je v budově', 'As soon as it is in the building') },
              {
                id: 'kc-b',
                label: t(
                  'Když se do něj vrtá, brousí nebo se láme',
                  'When it is drilled, sanded or broken',
                ),
              },
              { id: 'kc-c', label: t('Jen při požáru', 'Only in a fire') },
            ],
            correctChoiceId: 'kc-b',
            explanation: t(
              'Mechanické narušení uvolní vlákna do vzduchu - právě to dělá z běžné zakázky expozici azbestu.',
              'Mechanical disturbance releases fibres into the air - exactly what turns a routine job into asbestos exposure.',
            ),
          },
        ],
      },
      {
        id: 'ch-co-delat',
        title: t('Co dělat při podezření', 'What to do on suspicion'),
        minDwellSeconds: 20,
        requiredWidgetIds: ['ord-stop'],
        blocks: [
          {
            id: 'h-co-delat',
            blockType: 'heading',
            schemaVersion: 1,
            level: 2,
            text: t(
              'STOP - nevrtat, neuklízet, nahlásit',
              "STOP - don't drill, don't clean up, report",
            ),
          },
          {
            id: 'anim-stop',
            blockType: 'animation',
            schemaVersion: 1,
            scene: 'asbestosStop',
            steps: [
              {
                label: t('Stará šedá deska', 'Old grey board'),
                say: t('Podezření na azbest - stop práce.', 'Suspected asbestos - stop work.'),
              },
              {
                label: t('Nevrtat, nezametat, nevysávat', 'Do not drill, sweep or vacuum'),
                say: t('Jakékoli narušení uvolňuje vlákna.', 'Any disturbance releases fibres.'),
              },
              {
                label: t('Specializovaná firma a KHS', 'Licensed firm and KHS'),
                say: t(
                  'Odstranění jen oprávněná firma, ohlášení krajské hygienické stanici.',
                  'Removal only by a licensed firm, reported to the regional public health authority.',
                ),
              },
            ],
            caption: t(
              'Vrtání do eternitu STOP: práci přeruš, prostor neuklízej, předej specialistům.',
              "Drilling into asbestos board: STOP the work, don't clean up, hand over to specialists.",
            ),
          },
          {
            id: 'p-co-delat-1',
            blockType: 'paragraph',
            schemaVersion: 1,
            markdown: t(
              'Při podezření **okamžitě přeruš práci**. **Nezametej a nevysávej** běžným vysavačem (rozvíří vlákna), místnost uzavři, **informuj zákazníka** a doporuč **specializovanou firmu** - odstraňování azbestu se hlásí KHS a dělá se v kontrolovaném režimu. Přes FixIt zakázku ukonči s poznámkou o azbestu.',
              "On suspicion, **stop work immediately**. **Don't sweep or vacuum** with a regular vacuum (it re-suspends fibres), close off the room, **inform the customer** and refer a **licensed specialist firm** - asbestos removal is reported to the KHS and done under controlled conditions. Close the FixIt job with an asbestos note.",
            ),
          },
          {
            id: 'ord-stop',
            blockType: 'ordering',
            schemaVersion: 1,
            prompt: t(
              'Seřaď správnou reakci na podezření na azbest.',
              'Order the correct response to suspected asbestos.',
            ),
            items: [
              { id: 'os-1', label: t('Okamžitě přerušit práci', 'Stop work immediately') },
              {
                id: 'os-2',
                label: t('Neuklízet, uzavřít prostor', "Don't clean up, close off the area"),
              },
              { id: 'os-3', label: t('Informovat zákazníka', 'Inform the customer') },
              {
                id: 'os-4',
                label: t('Doporučit specializovanou firmu', 'Refer a licensed specialist'),
              },
            ],
          },
          {
            id: 'k-co-delat',
            blockType: 'keyTakeaway',
            schemaVersion: 1,
            markdown: t(
              'Tvá role je azbest **poznat a nedotknout se ho** - odstranění je licencovaná práce, nikdy součást běžné zakázky.',
              'Your job is to **recognise asbestos and leave it alone** - removal is licensed work, never part of a routine job.',
            ),
          },
        ],
      },
    ],
  },
  questions: [
    {
      id: 'q-reakce-single',
      kind: 'single',
      chapterId: 'ch-co-delat',
      prompt: t(
        'Při vrtání zjistíš, že deska je nejspíš eternit. Co uděláš?',
        'While drilling you realise the board is probably asbestos cement. What do you do?',
      ),
      choices: [
        {
          id: 'a',
          label: t(
            'Dovrtám opatrně a vysaji prach',
            'Finish the hole carefully and vacuum the dust',
          ),
        },
        {
          id: 'b',
          label: t(
            'Okamžitě přeruším práci a prostor neuklízím',
            "Stop immediately and don't clean up",
          ),
        },
        { id: 'c', label: t('Navlhčím desku a pokračuji', 'Wet the board and continue') },
      ],
      correct: 'b',
    },
    {
      id: 'q-klid-tf',
      kind: 'truefalse',
      chapterId: 'ch-proc',
      prompt: t(
        'Neporušená eternitová střecha sama o sobě neuvolňuje nebezpečná vlákna.',
        'An undamaged asbestos-cement roof does not by itself release dangerous fibres.',
      ),
      correct: true,
    },
    {
      id: 'q-kde-multi',
      kind: 'multi',
      chapterId: 'ch-kde',
      prompt: t(
        'Kde se azbest typicky vyskytuje? (vyber všechny správné)',
        'Where does asbestos typically occur? (select all that apply)',
      ),
      choices: [
        { id: 'a', label: t('Eternitová krytina', 'Corrugated asbestos-cement roofing') },
        { id: 'b', label: t('Izolace starých potrubí', 'Old pipe insulation') },
        { id: 'c', label: t('Novostavba z roku 2020', 'A 2020 new build') },
        { id: 'd', label: t('Boletické meziokenní panely', 'Boletice window infill panels') },
      ],
      correct: ['a', 'b', 'd'],
    },
    {
      id: 'q-rok-numeric',
      kind: 'numeric',
      chapterId: 'ch-kde',
      prompt: t(
        'Do kterého roku (přibližně) se azbest v ČR běžně používal ve stavebnictví?',
        'Until roughly what year was asbestos in common Czech construction use?',
      ),
      correct: 1990,
      tolerance: 0,
    },
    {
      id: 'q-stop-order',
      kind: 'ordering',
      chapterId: 'ch-co-delat',
      prompt: t(
        'Seřaď reakci na podezření na azbest.',
        'Order the response to suspected asbestos.',
      ),
      choices: [
        { id: 'a', label: t('Přerušit práci', 'Stop work') },
        { id: 'b', label: t('Neuklízet, uzavřít prostor', 'No clean-up, close the area') },
        { id: 'c', label: t('Informovat zákazníka', 'Inform the customer') },
        { id: 'd', label: t('Doporučit specialisty', 'Refer specialists') },
      ],
      correct: ['a', 'b', 'c', 'd'],
    },
  ],
};
